import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import _ from 'lodash';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { Subscription } from 'rxjs';
import { nullSafePath } from '../../shared/helpers/null-safe-path';
import { FeatureFlagService } from '../../shared/services/feature-flag.service';
import { SessionService } from '../../shared/services/session.service';
import { ApplicantService } from '../applicant-service/applicant.service';
import { Answer } from '../applicant-service/service-models/Answer';
import { IterativeApplicationRouterService } from '../iterative-application-router/iterative-application-router.service';
import { JobService } from '../job-service/job.service';
import { JobStatusModel } from '../job-service/models/job-status-model';
import { SurveyModel } from '../job-service/models/SurveyModel';
import { ApplicantElementViewed } from '../job-service/service-models/ApplicantElementViewed';
import { CustomApplicantElement } from '../job-service/service-models/CustomApplicantElement';
import { FileUploadQuestion } from '../job-service/service-models/FileUploadQuestion';
import { TextQuestion } from '../job-service/service-models/TextQuestion';
import { VideoQuestion } from '../job-service/service-models/VideoQuestion';
import { JobStatusService } from '../job-status.service';

@Component({
  selector: 'app-apply-survey',
  templateUrl: './apply-survey.component.html',
  styleUrls: ['./apply-survey.component.scss']
})
export class ApplySurveyComponent implements OnInit, OnDestroy {
  @ViewChild('cancelModal')
  cancelModal: ModalDirective;
  private subscription: Subscription = new Subscription();

  applicantElement = 'CustomApplicantElement';
  canNavigateBackwards: boolean;
  processingServerSideRequest = false;
  currentElement: CustomApplicantElement;
  identifier: string;
  subBrandIdentifier: string;
  jobId: number;
  applicantId: string;
  model: SurveyModel;
  subscriptions = new Subscription();
  openSaveAndFinishLaterModal = false;
  saveAndFinishLaterEnabled = false;
  attachmentDownloadRoute: string;
  
  constructor(private route: ActivatedRoute,
    private router: Router,
    private jobService: JobService,
    private jobStatusService: JobStatusService,
    private iterativeApplicationRouterService: IterativeApplicationRouterService,
    private applicantService: ApplicantService,
    private featureFlagService: FeatureFlagService,
    private readonly sessionService: SessionService) { }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    if (this.featureFlagService.flags !== undefined) {
      if (this.featureFlagService.flags['save-and-finish-later'] !== undefined) {
        this.saveAndFinishLaterEnabled = this.featureFlagService.flags['save-and-finish-later'];
      }
    }
    this.featureFlagService.flagChange.subscribe((flags) => {
      this.saveAndFinishLaterEnabled = flags['save-and-finish-later'];
    });

    let elementId: number;

    this.subscription.add(this.route.parent.params.subscribe((params: Params) => {
      this.identifier = params['identifier'];
      this.subBrandIdentifier = params['subBrandIdentifier'];
      this.jobId = params['jobId'];
      this.applicantId = params['applicantId'];
    }));

    this.subscription.add(this.route.params.subscribe((params: Params) => {
      elementId = parseInt(params['elementId'], 10);
    }));

    this.subscription.add(this.route.data
      .subscribe((data: { model: SurveyModel, status: JobStatusModel }) => {
        this.model = data.model;
        this.jobStatusService.checkIfPageAccessible(data.status, data.model.isFirstIterativeApplicationPart);
        this.currentElement = isNaN(elementId) || elementId === 0
          ? this.model.elements[0]
          : this.model.elements.find(x => x.id === elementId);
        this.applicantElement = this.currentElement.name;
        this.canNavigateBackwards = this.iterativeApplicationRouterService.canNavigateBackwards(
          this.model.yellowBrickRoad.orderableElements,
          this.currentElement.id);
        
        if (this.model.answers) {
          this.currentElement.questions.forEach(q => {
            const answers = this.model.answers.filter(a => a.questionId === q.id && a.questionVersion === q.version);
            if (answers && answers.length > 0) {
              q.answers = answers;
            }
          });
        }

        this.sendApplicantElementViewedEvent();
      })
    );
  }

  displayCancelModal() {
    this.cancelModal.show();
  }

  cancel() {
    this.router.navigate(nullSafePath(this.identifier, this.subBrandIdentifier));
  }

  currentElementIndex(): number {
    return this.model.elements.findIndex(x => x.id === this.currentElement.id);
  }

  get canSave(): boolean {
    return this.currentElement && !this.currentElement.questions.filter(x => x.isRequired).some(x => {
      return !x.answers
        || x.answers.length === 0
        || (x.type === 'Text' && (<TextQuestion>x).answers[0].value.length === 0)
        || (x.type === 'Video' && (<VideoQuestion>x).isValid === false)
        || (x.type === 'File' && (<FileUploadQuestion>x).isValid === false);
    });
  }

  movePrevious() {
    this.iterativeApplicationRouterService.back(
      nullSafePath(this.identifier, this.subBrandIdentifier),
      this.model.yellowBrickRoad.orderableElements,
      this.currentElement.id,
      this.jobId,
      this.applicantId);
  }

  updateCustomApplicantElement(emittedApplicantElement: CustomApplicantElement) {
    const customApplicantElement = _.find(this.model.elements,
      function (ae) { return ae.id === emittedApplicantElement.id; });

    customApplicantElement.questions = emittedApplicantElement.questions;
  }

  openSaveAndFinishLater() {
    this.openSaveAndFinishLaterModal = true;
  }

  saveAndFinishLater(emailAddress: string) {
    if (this.canSave) {
      this.save(true, emailAddress);
    } else {
      this.partiallySubmitApplication(emailAddress);
    }
  }

  partiallySubmitApplication(emailAddress: string) {
    const shouldTrimSubBrand = this.route.snapshot.params['subBrandIdentifier'] !== undefined ? 3 : 2;
    let routeArray = this.router.url.split('/');
    routeArray = routeArray.splice(shouldTrimSubBrand);
    const route = routeArray.join('/');

    this.subscription.add(this.applicantService.partiallySubmitApplication(emailAddress, route, this.applicantElement)
      .subscribe(
      data => {
        const identifiers = nullSafePath(this.identifier, this.subBrandIdentifier);

        const basePath = [...identifiers, 'jobs', this.jobId, 'apply', this.applicantId];
        this.router.navigate([...basePath, 'finish-later-thank-you']);
        this.processingServerSideRequest = false;
      },
      error => {
        this.processingServerSideRequest = false;
      })
    );
    this.closeSaveAndFinishLater();
  }

  closeSaveAndFinishLater() {
    this.openSaveAndFinishLaterModal = false;
  }

  save(partiallySubmitApplication = false, emailAddress = null) {
    if (this.processingServerSideRequest) {
      return;
    }

    this.processingServerSideRequest = true;

    const saveModel = new Array<Answer>();
    const files = new Array<File>();

    for (let i = 0; i < this.currentElement.questions.length; i++) {
      const question = this.currentElement.questions[i];

      if (question.file !== undefined) {
        files.push(question.file);
      }

      for (let j = 0; j < question.answers?.length; j++) {
        saveModel.push(question.answers[j]);
      }

      if (!question.isRequired && (!question.answers || question.answers.length === 0)) {
        const answer = new Answer();
        answer.questionId = question.id;
        answer.questionVersion = question.version;

        saveModel.push(answer);
      }
    }

    const applicationId = localStorage.getItem('applicationId');
    if (this.model.answers && this.model.answers.length > 0) {
      this.subscription.add(this.applicantService.updateSurvey(this.currentElement.id,
          applicationId,
          this.jobId,
          this.applicantId,
          saveModel,
          files)
        .subscribe(
          data => {
            if (partiallySubmitApplication) {
              this.partiallySubmitApplication(emailAddress);
            } else {
              this.moveNext();
              this.processingServerSideRequest = false;
            }
          },
          error => {
            this.processingServerSideRequest = false;
          }));
    } else {
      this.subscription.add(this.applicantService.uploadSurvey(this.currentElement.id, applicationId,
          this.jobId, this.applicantId, saveModel, files)
        .subscribe(
          data => {
            if (partiallySubmitApplication) {
              this.partiallySubmitApplication(emailAddress);
            } else {
              this.moveNext();
              this.processingServerSideRequest = false;
            }
          },
          error => {
            this.processingServerSideRequest = false;
          }));
    }
  }

  private moveNext() {
    this.iterativeApplicationRouterService.next(
      nullSafePath(this.identifier, this.subBrandIdentifier),
      this.model.yellowBrickRoad,
      this.currentElement.id,
      this.jobId,
      this.applicantId,
      this.model.isFirstIterativeApplicationPart,
      this.model.iterativeApplicationPartId);
  }

  private sendApplicantElementViewedEvent() {
    if (this.currentElement && !this.currentElement.viewedEventSent) {
      const event = new ApplicantElementViewed(
        this.jobId,
        new Date(),
        this.sessionService.getSessionId(),
        this.currentElement.applicantElementId,
        this.currentElement.version,
        this.currentElement.sortOrder);
      this.subscriptions.add(this.jobService.trackApplicantEvent(this.jobId, event).subscribe());
      if (this.currentElement) {
        this.currentElement.viewedEventSent = true;
      }
    }
  }
}
